import { useState, type ReactElement } from 'react';
import { BEREICH_FARBEN, BEREICH_FARBWERTE, BEREICH_VORSCHLAEGE, artVon, artZumNamen, freierBereichsname, type Op } from '@schnitt/shared';
import { useEditor } from '../store';
import { Sheet } from './Sheet';

/**
 * **Ein Bereich, vom Band aus** (7.166).
 *
 * Sascha, 24. September: „das mit den Klammern ist ganz gut für die Bereiche.
 * Aber ich sollte sie auch benennen können. Und irgendwie anheften oderso.“
 * Umbenennen ging bis dahin nur im Export-Blatt, zwei Tipps tief hinter
 * „Ändern“ -- und niemand sucht den Namen eines Bandes auf der Zeitleiste im
 * Export. Jetzt öffnet ein Tipp auf den Namen im Band dieses Blatt: Name,
 * Farbe, Anheften, Entfernen.
 *
 * Die Vorschläge stehen als Knöpfe da, damit auf dem Telefon niemand
 * tippen muss, um „Story“ zu heissen -- und jeder bringt seine Farbe mit.
 *
 * **Gleich nach dem Anlegen** kommt dasselbe Blatt als „Neuer Bereich“
 * (Sascha, 30. September: „Nach dem Anlegen von einem Bereich nach dem Namen
 * und Farbe fragen“). Die Wahl beim Anheften ist zugleich die Einstellung für
 * die nächsten: „Dann noch Einstellungen ob der Bereichmarker wo anheftet“.
 */
export function BereichSheet({
  bereichId,
  onClose,
  neu = false,
}: {
  bereichId: string;
  onClose: () => void;
  /** Gerade angelegt: das Blatt fragt nach Name und Farbe. */
  neu?: boolean;
}): ReactElement {
  const timeline = useEditor((s) => s.timeline);
  const dispatch = useEditor((s) => s.dispatch);
  const role = useEditor((s) => s.role);
  const toast = useEditor((s) => s.toast);
  const assets = useEditor((s) => s.assets);
  const b = timeline.exportbereiche.find((x) => x.id === bereichId) ?? null;
  const [name, setName] = useState(b?.name ?? '');

  if (!b) {
    return (
      <Sheet title="Bereich" onClose={onClose}>
        <p className="hint">Diesen Bereich gibt es nicht mehr.</p>
      </Sheet>
    );
  }

  const editable = role !== 'viewer';
  const patch = (werte: Record<string, unknown>) =>
    dispatch([{ op: 'updateExportbereich', bereichId: b.id, patch: werte }] as Op[]);
  /** Anheften am Bereich -- und als Einstellung für die nächsten. */
  const heften = (an: boolean) =>
    dispatch([
      { op: 'updateExportbereich', bereichId: b.id, patch: { angeheftet: an } },
      { op: 'updateSettings', patch: { bereicheAnheften: an } },
    ] as Op[]);
  const benenne = (neu: string) => {
    const sauber = neu.trim().slice(0, 60);
    if (!sauber || sauber === b.name) return;
    setName(sauber);
    patch({ name: sauber });
  };
  /** Woran er hängt -- nur, was es noch gibt; sonst gilt der Film. */
  const alleClips = timeline.tracks.flatMap((tr) => tr.clips);
  const gewaehlt =
    b.heftenAn?.clip && alleClips.some((c) => c.id === b.heftenAn!.clip)
      ? `clip:${b.heftenAn.clip}`
      : b.heftenAn?.spur && timeline.tracks.some((tr) => tr.id === b.heftenAn!.spur && tr.clips.length > 0)
        ? `spur:${b.heftenAn.spur}`
        : null;
  const basis = timeline.tracks.find((tr) => tr.kind === 'video' && tr.clips.length > 0);
  const spuren = timeline.tracks.filter((tr) => tr.clips.length > 0 && tr.id !== basis?.id);
  const clips = alleClips
    .filter((c) => (c.start < b.bis && c.start + c.duration > b.von) || `clip:${c.id}` === gewaehlt)
    .sort((x, y) => x.start - y.start)
    .slice(0, 8);
  const clipName = (c: (typeof alleClips)[number]): string => {
    const asset = c.source.kind === 'asset' ? assets.find((a) => a.id === (c.source as { assetId: string }).assetId) : undefined;
    return (c.label || asset?.name || (c.source.kind === 'text' ? (c.source as { text: string }).text : '') || 'Clip').slice(0, 28);
  };
  const heftenAn = (an: { spur?: string; clip?: string }) =>
    dispatch([{ op: 'updateExportbereich', bereichId: b.id, patch: { heftenAn: an } }] as Op[]);
  const zeit = (s: number) => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, '0')}`;

  return (
    <Sheet title={neu ? 'Neuer Bereich' : b.name} onClose={onClose}>
      <p className="hint" style={{ marginTop: 0 }} data-bereich-blatt={b.id} data-bereich-neu-blatt={neu ? 'ja' : undefined}>
        {neu ? 'Angelegt: ' : ''}
        {zeit(b.von)} bis {zeit(b.bis)}
        {neu ? '. Wie soll er heissen, und in welcher Farbe?' : ''}
      </p>

      <form
        className="field"
        onSubmit={(e) => {
          e.preventDefault();
          benenne(name);
        }}
      >
        <span>Name</span>
        <input
          value={name}
          maxLength={60}
          disabled={!editable}
          data-bereich-name-feld
          onChange={(e) => setName(e.target.value)}
          onBlur={() => benenne(name)}
        />
      </form>
      {/* **Die Art** (7.196; Sascha, 3. Oktober: „name und art des
          bereichs“). Sie bringt die Farbe mit; der Name bleibt frei. Nur wer
          noch einen Namen aus den Vorschlägen trägt („Story 2“), heisst
          danach wie die neue Art -- ein eigener Name bleibt stehen. */}
      <div className="field" style={{ marginTop: 14 }}>
        <span>Art</span>
        <div className="chips">
          {BEREICH_VORSCHLAEGE.map((v) => (
            <button
              key={v.art}
              className={`chip${artVon(b) === v.art ? ' active' : ''}`}
              data-bereich-art={v.art}
              data-bereich-name-vorschlag={v.name}
              disabled={!editable}
              onClick={() => {
                const werte: Record<string, unknown> = { art: v.art, farbe: v.farbe };
                // Auch „Bereich“ aus der Auswahl (7.197) heisst danach wie seine Art.
                if (artZumNamen(b.name) !== null || /^Bereich( \d+)?$/.test(b.name)) {
                  const frei = freierBereichsname(timeline.exportbereiche, v.name, b.id);
                  setName(frei);
                  werte.name = frei;
                }
                patch(werte);
              }}
            >
              <span className="bereich-punkt" style={{ background: BEREICH_FARBWERTE[v.farbe] }} aria-hidden="true" />
              {v.name}
            </button>
          ))}
        </div>
      </div>

      <div className="field" style={{ marginTop: 14 }}>
        <span>Farbe</span>
        <div className="chips">
          {BEREICH_FARBEN.map((f) => (
            <button
              key={f}
              className={`chip farbwahl${b.farbe === f ? ' active' : ''}`}
              data-bereich-farbwahl={f}
              aria-label={f}
              disabled={!editable}
              style={{ ['--bereich' as string]: BEREICH_FARBWERTE[f] }}
              onClick={() => patch({ farbe: f })}
            />
          ))}
        </div>
      </div>

      <div className="field" style={{ marginTop: 14 }}>
        <span>Anheften</span>
        <div className="chips">
          <button
            className={`chip${b.angeheftet ? ' active' : ''}`}
            data-bereich-anheften="an"
            disabled={!editable}
            onClick={() => {
              if (!b.angeheftet) {
                heften(true);
                if (!neu) toast('success', `„${b.name}“ hält sich jetzt an seine Clips.`);
              }
            }}
          >
            An den Clips
          </button>
          <button
            className={`chip${!b.angeheftet ? ' active' : ''}`}
            data-bereich-anheften="aus"
            disabled={!editable}
            onClick={() => {
              if (b.angeheftet) heften(false);
            }}
          >
            Fest auf der Zeit
          </button>
        </div>
        <p className="hint" data-bereich-anheften-satz>
          {b.angeheftet
            ? 'Anfang und Ende hängen an ihren Bildern. Fügst du davor etwas ein, rückt der Bereich mit; kommt innen etwas dazu, wird er länger.'
            : 'Der Bereich bleibt auf der Zeit stehen, auch wenn davor etwas eingefügt wird.'}{' '}
          Neue Bereiche fangen auch so an.
        </p>
        {/* **Woran** (7.196; Sascha, 3. Oktober: „bereich anpinnen (wandert
            mit bei elementXY)“): die unterste Bildspur wie bisher, eine
            andere Spur, oder ein Clip -- dann wandert der ganze Bereich mit
            ihm. Zur Wahl stehen die Spuren mit Clips und die Clips, die im
            Bereich liegen. */}
        {b.angeheftet ? (
          <div className="chips" style={{ marginTop: 6 }} data-bereich-woran>
            <button
              className={`chip${!gewaehlt ? ' active' : ''}`}
              data-bereich-heften-an="film"
              disabled={!editable}
              onClick={() => heftenAn({})}
            >
              Film (unterste Spur)
            </button>
            {spuren.map((tr) => (
              <button
                key={tr.id}
                className={`chip${gewaehlt === `spur:${tr.id}` ? ' active' : ''}`}
                data-bereich-heften-an={`spur:${tr.id}`}
                disabled={!editable}
                onClick={() => heftenAn({ spur: tr.id })}
              >
                Spur „{tr.name || tr.id}“
              </button>
            ))}
            {clips.map((c) => (
              <button
                key={c.id}
                className={`chip${gewaehlt === `clip:${c.id}` ? ' active' : ''}`}
                data-bereich-heften-an={`clip:${c.id}`}
                disabled={!editable}
                onClick={() => heftenAn({ clip: c.id })}
              >
                {clipName(c)} · {zeit(c.start)}
              </button>
            ))}
          </div>
        ) : null}
      </div>

      {editable ? (
        <button
          style={{ width: '100%', marginTop: 10 }}
          data-bereich-entfernen
          onClick={() => {
            dispatch([{ op: 'removeExportbereich', bereichId: b.id }] as Op[]);
            toast('info', `„${b.name}“ entfernt.`);
            onClose();
          }}
        >
          Bereich entfernen
        </button>
      ) : null}
    </Sheet>
  );
}
